import { sizes } from '@dixa/lib-shared-tokens';
import { QueueWithAssignmentCounts } from 'lib-shared-types-api';
import { memo, useLayoutEffect, useRef, useState } from 'react';

import { useServerStorage } from 'utils/useServerStorage';

import { QueueDashboardQueue } from '../queues/components/queue/queueDashboardQueue';
import { QueueDashboardTable } from '../queues/components/table/queueDashboardTable';

type QueueDashboardLoadingProps = {
  contentHeight?: number;
};

export const QueueDashboardLoading = memo(function QueueDashboardLoading({
  contentHeight,
}: QueueDashboardLoadingProps): JSX.Element | null {
  const itemRef = useRef<HTMLDivElement | HTMLTableRowElement>(null);
  const [itemHeight, setItemHeight] = useState<number>();

  const storage = useServerStorage('user');
  const { layout } = storage.data.dashboard ?? {};

  useLayoutEffect(
    () => setItemHeight(itemRef.current?.getBoundingClientRect().height),
    [layout],
  );

  const renderSkeletons = (): JSX.Element[] | null => {
    if (!contentHeight || !itemHeight) return null;

    const gap = layout === 'compact' ? 0 : parseInt(sizes.spacer.values[300]);
    const count = Math.floor(contentHeight / (itemHeight + gap));

    return Array.from({ length: count }).map((_, i) => (
      <QueueDashboardQueue
        key={i + 1}
        queue={skeletonQueue}
        index={i + 1}
        totalQueues={count}
        isSkeleton
      />
    ));
  };

  if (layout === 'compact') {
    return (
      <QueueDashboardTable>
        <QueueDashboardQueue
          ref={itemRef}
          queue={skeletonQueue}
          index={0}
          totalQueues={1}
          isSkeleton
        />
        {renderSkeletons()}
      </QueueDashboardTable>
    );
  }

  return (
    <>
      <QueueDashboardQueue
        ref={itemRef}
        queue={skeletonQueue}
        index={0}
        totalQueues={1}
        isSkeleton
      />
      {renderSkeletons()}
    </>
  );
});

const skeletonQueue: QueueWithAssignmentCounts = {
  id: '743e74cc-d653-455f-a39f-a97d6d6e397e',
  name: 'Example queue',
  is_default: false,
  is_archived: false,
  is_restricted: false,
  queue_thresholds: {},
  calculation_method: 'abandoned_ignored',
  offer_timeout: null,
  offer_algorithm: 'all_at_once',
  offer_abandoned_conversations: false,
  available_offer_algorithms: [
    'all_at_once',
    'one_at_a_time_random',
    'agent_priority_all_at_once',
    'agent_priority_one_at_a_time_random',
    'longest_idle',
    'agent_priority_longest_idle',
  ],
  member_list_type: null,
  priority: null,
  wrapup_timeout: null,
  do_not_offer_timeouts: {
    facebook_messenger: 300,
    email: 43200,
    sms: 300,
    voicemail: 300,
    widget_chat: 300,
    pstn_phone: 300,
    callback: 300,
    whats_app: 300,
    contact_form: 43200,
  },
  is_do_not_offer_enabled: false,
  preferred_agent_timeouts: {
    facebook_messenger: 300,
    email: 43200,
    sms: 300,
    voicemail: 300,
    widget_chat: 300,
    pstn_phone: 300,
    callback: 300,
    whats_app: 300,
    contact_form: 43200,
  },
  is_preferred_agent_enabled: true,
  preferred_agent_offline_timeout: null,
  personal_agent_offline_timeout: null,
  number_of_teams: 1,
  number_of_agents: 2,
};
